import React, { useEffect, useState } from 'react';
import { 
  getLabIntegrations, 
  addLabIntegration, 
  updateLabIntegration, 
  deleteLabIntegration,
  testLabIntegrationConnection,
  getWebhookInfo,
  generateWebhookSecret,
  updateLabIntegrationCredentials
} from '../../services/labIntegrationService';
import { getAllFacilities } from '../../services/facilityService';
import { 
  Box, Typography, Button, TextField, Select, MenuItem, Table, TableHead, 
  TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, 
  DialogActions, FormControl, InputLabel, Switch, FormControlLabel, Grid, 
  Checkbox, IconButton, Paper, TableContainer, Tabs, Tab, Alert, Chip,
  CircularProgress, Tooltip, Divider, Accordion, AccordionSummary, 
  AccordionDetails, InputAdornment, Snackbar, TablePagination
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MacroTextBox from '../utils/MacroTextBox';
import FacilityAssignmentTree from '../common/FacilityAssignmentTree';
import BusinessIcon from '@mui/icons-material/Business';

const USAGE_OPTIONS = ['Debugging', 'Production'];
const PROTOCOL_OPTIONS = ['SFTP', 'API', 'File Storage'];
const DIRECTION_OPTIONS = ['Bidirectional', 'Inbound', 'Outbound'];
const DATA_FORMAT_OPTIONS = ['HL7', 'FHIR', 'BOTH'];
const FHIR_VERSION_OPTIONS = ['R4', 'STU3'];
const MODALITY_OPTIONS = ['CT', 'MR', 'XR', 'US', 'NM', 'PT', 'DX', 'CR', 'MG', 'RF', 'SC'];

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Initial form state
const initialFormState = {
  active: true,
  name: '',
  npi: '',
  usage: 'Debugging',
  assignedFacilities: [],
  // HL7 Configuration
  senderApp: '',
  senderFacility: '',
  receiverApp: '',
  receiverFacility: '',
  // Protocol & Direction
  protocol: 'SFTP',
  direction: 'Bidirectional',
  dataFormat: 'HL7',
  // SFTP Configuration
  loginId: '',
  password: '',
  remoteHost: '',
  port: 22,
  ordersPath: '',
  resultsPath: '',
  // API Configuration
  apiConfig: {
    baseUrl: '',
    apiKey: '',
    apiKeyHeader: 'X-API-Key',
    timeout: 30000,
    ordersEndpoint: '/orders',
    resultsEndpoint: '/results',
    imagingEndpoint: '/imaging',
    webhookSecret: ''
  },
  // FHIR Configuration
  fhirConfig: {
    version: 'R4',
    serviceRequestEndpoint: '/ServiceRequest',
    diagnosticReportEndpoint: '/DiagnosticReport',
    imagingStudyEndpoint: '/ImagingStudy',
    bundleEndpoint: '/Bundle'
  },
  // Imaging Configuration
  imagingConfig: {
    pacsUrl: '',
    dicomSupport: false,
    wadoUrl: '',
    modalities: []
  },
  notes: ''
};

export default function LabIntegration() {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editId, setEditId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchIntegrations(), fetchFacilities()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await getLabIntegrations();
      if (response?.labIntegrations && Array.isArray(response.labIntegrations)) {
        setIntegrations(response.labIntegrations);
      } else {
        setIntegrations([]);
      }
    } catch (error) {
      console.error('Error fetching lab integrations:', error);
      setIntegrations([]);
    }
  };

  const fetchFacilities = async () => {
    try {
      const facilitiesData = await getAllFacilities();
      if (Array.isArray(facilitiesData)) {
        setFacilities(facilitiesData);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleOpen = (integration) => {
    if (integration) {
      // Merge existing integration data with default form state to handle missing fields
      setForm({
        ...initialFormState,
        ...integration,
        apiConfig: { ...initialFormState.apiConfig, ...integration.apiConfig },
        fhirConfig: { ...initialFormState.fhirConfig, ...integration.fhirConfig },
        imagingConfig: { ...initialFormState.imagingConfig, ...integration.imagingConfig }
      });
      setEditId(integration.id);
    } else {
      setForm(initialFormState);
      setEditId(null);
    }
    setTabValue(0);
    setTestResult(null);
    setWebhookInfo(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setTestResult(null);
    setWebhookInfo(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNestedChange = (section, field, value) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleModalitiesChange = (modality) => {
    const currentModalities = form.imagingConfig.modalities || [];
    const newModalities = currentModalities.includes(modality)
      ? currentModalities.filter(m => m !== modality)
      : [...currentModalities, modality];
    handleNestedChange('imagingConfig', 'modalities', newModalities);
  };

  const handleSubmit = async () => {
    try {
      if (editId) {
        await updateLabIntegration(editId, form);
        setSnackbar({ open: true, message: 'Integration updated successfully', severity: 'success' });
      } else {
        await addLabIntegration(form);
        setSnackbar({ open: true, message: 'Integration created successfully', severity: 'success' });
      }
      await fetchIntegrations();
      handleClose();
    } catch (error) {
      setSnackbar({ open: true, message: `Error saving integration: ${error.message}`, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      try {
        await deleteLabIntegration(id);
        await fetchIntegrations();
        setSnackbar({ open: true, message: 'Integration deleted successfully', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: `Error deleting integration: ${error.message}`, severity: 'error' });
      }
    }
  };

  const handleTestConnection = async () => {
    if (!editId) {
      setSnackbar({ open: true, message: 'Please save the integration first before testing', severity: 'warning' });
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const result = await testLabIntegrationConnection(editId);
      setTestResult(result.testResult);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message,
        responseTime: 0
      });
    } finally {
      setTesting(false);
    }
  };

  const handleFetchWebhookInfo = async () => {
    if (!editId) return;
    
    try {
      const info = await getWebhookInfo(editId);
      setWebhookInfo(info);
    } catch (error) {
      setSnackbar({ open: true, message: `Error fetching webhook info: ${error.message}`, severity: 'error' });
    }
  };

  const handleGenerateWebhookSecret = async () => {
    if (!editId) return;
    
    try {
      const result = await generateWebhookSecret(editId);
      setSnackbar({ open: true, message: 'Webhook secret generated! Store it securely.', severity: 'success' });
      handleNestedChange('apiConfig', 'webhookSecret', result.webhookSecret);
    } catch (error) {
      setSnackbar({ open: true, message: `Error generating webhook secret: ${error.message}`, severity: 'error' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'info' });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated integrations
  const paginatedIntegrations = Array.isArray(integrations) 
    ? integrations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  const getStatusChip = (integration) => {
    if (integration.lastTestSuccess === true) {
      return <Chip icon={<CheckCircleIcon />} label="Connected" color="success" size="small" />;
    } else if (integration.lastTestSuccess === false) {
      return <Chip icon={<ErrorIcon />} label="Connection Failed" color="error" size="small" />;
    }
    return <Chip label="Not Tested" size="small" variant="outlined" />;
  };

  const getCredentialStatusChip = (integration) => {
    if (integration.credentialsInSecretManager) {
      return (
        <Tooltip title="Credentials stored securely in Google Secret Manager">
          <Chip 
            icon={<CheckCircleIcon />} 
            label="Secured" 
            color="success" 
            size="small" 
            variant="outlined"
          />
        </Tooltip>
      );
    }
    if (integration.hasPassword || integration.hasPrivateKey || integration.apiConfig?.hasApiKey) {
      return (
        <Tooltip title="Credentials configured">
          <Chip label="Configured" size="small" variant="outlined" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="No credentials configured">
        <Chip label="None" size="small" variant="outlined" color="warning" />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        borderRadius: '16px', 
        mb: 3,
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        py: 1.5,
        px: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                Lab Integration
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Configure laboratory and imaging system integrations (HL7 / FHIR)
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            size="large"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Add Integration
          </Button>
        </Box>
      </Box>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          py: 8 
        }}>
          <CircularProgress size={48} sx={{ color: '#667eea', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading lab integrations...
          </Typography>
        </Box>
      )}

      {/* Integration List */}
      {!loading && (
      <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Protocol</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Data Format</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Direction</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Credentials</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedIntegrations.map(integration => (
              <TableRow key={integration.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon fontSize="small" color="action" />
                    {integration.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={integration.protocol} 
                    size="small" 
                    color={integration.protocol === 'API' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={integration.dataFormat || 'HL7'} 
                    size="small" 
                    color={integration.dataFormat === 'FHIR' ? 'secondary' : integration.dataFormat === 'BOTH' ? 'info' : 'default'}
                  />
                </TableCell>
                <TableCell>{integration.direction || 'Bidirectional'}</TableCell>
                <TableCell>{getCredentialStatusChip(integration)}</TableCell>
                <TableCell>{getStatusChip(integration)}</TableCell>
                <TableCell>
                  <Chip 
                    label={integration.active ? 'Active' : 'Inactive'} 
                    color={integration.active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Button onClick={() => handleOpen(integration)} size="small" sx={{ mr: 1 }}>Edit</Button>
                  <Button onClick={() => handleDelete(integration.id)} color="error" size="small">Delete</Button>
                </TableCell>
              </TableRow>
            ))}
            {(!integrations || integrations.length === 0) && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No integrations configured yet</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={integrations?.length || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
        />
      </TableContainer>
      )}

      {/* Integration Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{editId ? 'Edit' : 'Add'} Lab Integration</Typography>
            {editId && (
              <Button
                variant="outlined"
                startIcon={testing ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleTestConnection}
                disabled={testing}
                size="small"
              >
                Test Connection
              </Button>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {/* Test Result Alert */}
          {testResult && (
            <Alert 
              severity={testResult.success ? 'success' : 'error'} 
              sx={{ m: 2, mb: 0 }}
              icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <span>{testResult.success ? 'Connection successful!' : `Connection failed: ${testResult.error}`}</span>
                {testResult.responseTime > 0 && (
                  <Chip 
                    icon={<SpeedIcon />} 
                    label={`${testResult.responseTime}ms`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label="Basic Info" icon={<SettingsIcon />} iconPosition="start" />
              <Tab label="Protocol Config" icon={<LinkIcon />} iconPosition="start" />
              <Tab label="Data Format" />
              <Tab label="Imaging / DICOM" />
              <Tab label="Facilities" icon={<BusinessIcon />} iconPosition="start" />
              {editId && <Tab label="Webhook" />}
            </Tabs>
          </Box>

          {/* Tab 0: Basic Info */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={form.active} onChange={handleChange} name="active" color="success" />}
                    label="Integration Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Integration Name" name="name" value={form.name} onChange={handleChange} fullWidth required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="NPI" name="npi" value={form.npi} onChange={handleChange} fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Usage</InputLabel>
                    <Select label="Usage" name="usage" value={form.usage} onChange={handleChange}>
                      {USAGE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>HL7 Sender/Receiver IDs</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Sender Application" name="senderApp" value={form.senderApp} onChange={handleChange} fullWidth helperText="MSH-3" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Sender Facility" name="senderFacility" value={form.senderFacility} onChange={handleChange} fullWidth helperText="MSH-4" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Receiver Application" name="receiverApp" value={form.receiverApp} onChange={handleChange} fullWidth helperText="MSH-5" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Receiver Facility" name="receiverFacility" value={form.receiverFacility} onChange={handleChange} fullWidth helperText="MSH-6" />
                </Grid>
                
                <Grid item xs={12}>
                  <MacroTextBox
                    label="Notes"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* Tab 1: Protocol Config */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Protocol</InputLabel>
                    <Select label="Protocol" name="protocol" value={form.protocol} onChange={handleChange}>
                      {PROTOCOL_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Direction</InputLabel>
                    <Select label="Direction" name="direction" value={form.direction} onChange={handleChange}>
                      {DIRECTION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {/* SFTP Configuration */}
                {form.protocol === 'SFTP' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>SFTP Configuration</Typography>
                        {form.credentialsInSecretManager && (
                          <Chip 
                            icon={<CheckCircleIcon />} 
                            label="Credentials Secured" 
                            color="success" 
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Remote Host" name="remoteHost" value={form.remoteHost} onChange={handleChange} fullWidth placeholder="sftp.labpartner.com" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Port" name="port" type="number" value={form.port || 22} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Username" name="loginId" value={form.loginId} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label={form.hasPassword ? "Password (configured)" : "Password"}
                        name="password" 
                        value={form.password || ''} 
                        onChange={handleChange} 
                        fullWidth 
                        type={showPassword ? 'text' : 'password'}
                        placeholder={form.hasPassword ? "Enter new password to change" : "Enter password"}
                        helperText={form.credentialsInSecretManager ? "🔒 Stored securely in Secret Manager" : "Will be stored securely"}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Orders Path" name="ordersPath" value={form.ordersPath} onChange={handleChange} fullWidth placeholder="/outbound/orders" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Results Path" name="resultsPath" value={form.resultsPath} onChange={handleChange} fullWidth placeholder="/inbound/results" />
                    </Grid>
                    {/* SSH Private Key option */}
                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2">
                            SSH Private Key Authentication (Optional)
                            {form.hasPrivateKey && <Chip label="Configured" size="small" color="success" sx={{ ml: 1 }} />}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TextField
                            label="SSH Private Key"
                            name="privateKey"
                            value={form.privateKey || ''}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;...&#10;-----END OPENSSH PRIVATE KEY-----"
                            helperText="Paste your SSH private key here. Will be stored securely in Secret Manager."
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </>
                )}

                {/* API Configuration */}
                {form.protocol === 'API' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>API Configuration</Typography>
                        {form.credentialsInSecretManager && (
                          <Chip 
                            icon={<CheckCircleIcon />} 
                            label="Credentials Secured" 
                            color="success" 
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="Base URL" 
                        value={form.apiConfig?.baseUrl || ''} 
                        onChange={(e) => handleNestedChange('apiConfig', 'baseUrl', e.target.value)} 
                        fullWidth 
                        placeholder="https://api.labpartner.com/v1"
                        helperText="The base URL for all API requests"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label={form.apiConfig?.hasApiKey ? "API Key (configured)" : "API Key"}
                        value={form.apiConfig?.apiKey || ''} 
                        onChange={(e) => handleNestedChange('apiConfig', 'apiKey', e.target.value)} 
                        fullWidth 
                        type={showApiKey ? 'text' : 'password'}
                        placeholder={form.apiConfig?.hasApiKey ? "Enter new API key to change" : "Enter API key"}
                        helperText={form.credentialsInSecretManager ? "🔒 Stored securely in Secret Manager" : "Will be stored securely"}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                                {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="API Key Header Name" 
                        value={form.apiConfig?.apiKeyHeader || 'X-API-Key'} 
                        onChange={(e) => handleNestedChange('apiConfig', 'apiKeyHeader', e.target.value)} 
                        fullWidth 
                        helperText="Header name for API key authentication"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="Timeout (ms)" 
                        type="number"
                        value={form.apiConfig?.timeout || 30000} 
                        onChange={(e) => handleNestedChange('apiConfig', 'timeout', parseInt(e.target.value))} 
                        fullWidth 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="Orders Endpoint" 
                        value={form.apiConfig?.ordersEndpoint || '/orders'} 
                        onChange={(e) => handleNestedChange('apiConfig', 'ordersEndpoint', e.target.value)} 
                        fullWidth 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="Results Endpoint" 
                        value={form.apiConfig?.resultsEndpoint || '/results'} 
                        onChange={(e) => handleNestedChange('apiConfig', 'resultsEndpoint', e.target.value)} 
                        fullWidth 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="Imaging Endpoint" 
                        value={form.apiConfig?.imagingEndpoint || '/imaging'} 
                        onChange={(e) => handleNestedChange('apiConfig', 'imagingEndpoint', e.target.value)} 
                        fullWidth 
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </TabPanel>

          {/* Tab 2: Data Format */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Data Format</InputLabel>
                    <Select label="Data Format" name="dataFormat" value={form.dataFormat || 'HL7'} onChange={handleChange}>
                      {DATA_FORMAT_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>
                          {opt === 'HL7' && 'HL7 v2.x'}
                          {opt === 'FHIR' && 'FHIR R4'}
                          {opt === 'BOTH' && 'Both HL7 & FHIR'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {(form.dataFormat === 'FHIR' || form.dataFormat === 'BOTH') && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>FHIR Configuration</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>FHIR Version</InputLabel>
                        <Select 
                          label="FHIR Version" 
                          value={form.fhirConfig?.version || 'R4'} 
                          onChange={(e) => handleNestedChange('fhirConfig', 'version', e.target.value)}
                        >
                          {FHIR_VERSION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="ServiceRequest Endpoint" 
                        value={form.fhirConfig?.serviceRequestEndpoint || '/ServiceRequest'} 
                        onChange={(e) => handleNestedChange('fhirConfig', 'serviceRequestEndpoint', e.target.value)} 
                        fullWidth 
                        helperText="For outbound lab orders"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="DiagnosticReport Endpoint" 
                        value={form.fhirConfig?.diagnosticReportEndpoint || '/DiagnosticReport'} 
                        onChange={(e) => handleNestedChange('fhirConfig', 'diagnosticReportEndpoint', e.target.value)} 
                        fullWidth 
                        helperText="For inbound results"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="ImagingStudy Endpoint" 
                        value={form.fhirConfig?.imagingStudyEndpoint || '/ImagingStudy'} 
                        onChange={(e) => handleNestedChange('fhirConfig', 'imagingStudyEndpoint', e.target.value)} 
                        fullWidth 
                        helperText="For imaging references"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="Bundle Endpoint" 
                        value={form.fhirConfig?.bundleEndpoint || '/Bundle'} 
                        onChange={(e) => handleNestedChange('fhirConfig', 'bundleEndpoint', e.target.value)} 
                        fullWidth 
                        helperText="For batch operations"
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>HL7:</strong> Uses ORM^O01 for orders, ORU^R01 for results, OMI^O23 for imaging orders.<br />
                      <strong>FHIR:</strong> Uses ServiceRequest for orders, DiagnosticReport for results, ImagingStudy for imaging.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* Tab 3: Imaging / DICOM */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={form.imagingConfig?.dicomSupport || false} 
                        onChange={(e) => handleNestedChange('imagingConfig', 'dicomSupport', e.target.checked)} 
                        color="primary"
                      />
                    }
                    label="Enable DICOM / Imaging Support"
                  />
                </Grid>

                {form.imagingConfig?.dicomSupport && (
                  <>
                    <Grid item xs={12}>
                      <TextField 
                        label="PACS URL" 
                        value={form.imagingConfig?.pacsUrl || ''} 
                        onChange={(e) => handleNestedChange('imagingConfig', 'pacsUrl', e.target.value)} 
                        fullWidth 
                        placeholder="https://pacs.hospital.com"
                        helperText="Picture Archiving and Communication System URL"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="WADO URL" 
                        value={form.imagingConfig?.wadoUrl || ''} 
                        onChange={(e) => handleNestedChange('imagingConfig', 'wadoUrl', e.target.value)} 
                        fullWidth 
                        placeholder="https://pacs.hospital.com/wado"
                        helperText="Web Access to DICOM Objects URL for image retrieval"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Supported Modalities</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {MODALITY_OPTIONS.map(modality => (
                          <Chip
                            key={modality}
                            label={modality}
                            onClick={() => handleModalitiesChange(modality)}
                            color={(form.imagingConfig?.modalities || []).includes(modality) ? 'primary' : 'default'}
                            variant={(form.imagingConfig?.modalities || []).includes(modality) ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        CT=Computed Tomography, MR=MRI, XR=X-Ray, US=Ultrasound, NM=Nuclear Medicine, PT=PET, DX=Digital Radiography, CR=Computed Radiography, MG=Mammography, RF=Fluoroscopy, SC=Secondary Capture
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </TabPanel>

          {/* Tab 4: Facilities */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Assign Facilities
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which facilities can use this lab integration. Orders from these facilities will be routed to this lab.
              </Typography>
              <FacilityAssignmentTree
                selectedFacilities={form.assignedFacilities || []}
                onChange={(facilityIds) => setForm(prev => ({ ...prev, assignedFacilities: facilityIds }))}
                showSummary={true}
                maxHeight={350}
              />
            </Box>
          </TabPanel>

          {/* Tab 5: Webhook (only for existing integrations) */}
          {editId && (
            <TabPanel value={tabValue} index={5}>
              <Box sx={{ p: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  External labs can send results to your system using the webhook URL below. 
                  They can send FHIR DiagnosticReport/Bundle or HL7 ORU messages.
                </Alert>

                <Button 
                  variant="outlined" 
                  onClick={handleFetchWebhookInfo}
                  sx={{ mb: 2 }}
                >
                  Get Webhook URL
                </Button>

                {webhookInfo && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Webhook URL</Typography>
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField 
                        value={webhookInfo.webhookUrl} 
                        fullWidth 
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                      <Tooltip title="Copy URL">
                        <IconButton onClick={() => copyToClipboard(webhookInfo.webhookUrl)}>
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Paper>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Supported Formats</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {webhookInfo.supportedFormats?.map((format, i) => (
                        <Chip key={i} label={format} size="small" />
                      ))}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Authentication</Typography>
                    <Typography variant="body2" color="text.secondary">{webhookInfo.authentication}</Typography>

                    <Box sx={{ mt: 2 }}>
                      <Button 
                        variant="contained" 
                        color="warning" 
                        onClick={handleGenerateWebhookSecret}
                        size="small"
                      >
                        Generate New Webhook Secret
                      </Button>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                        This will invalidate any previously generated secret
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </TabPanel>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editId ? 'Update' : 'Create'} Integration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
